import { Fragment, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { correctionsApi } from '../../api/corrections'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { formatRelative } from '../../lib/format'
import { factsOf } from '../../lib/improve'
import type { Turn } from '../../types/corrections'

// "Chat about why it's incorrect." The person says what the preset could not, and the model asks what it
// needs and ends at a typed hypothesis — the thing the nightly loop actually reads. With no model
// configured the thread still records the person's words, and says so rather than pretending at a reply.

export function CorrectionThread({ correctionId, compact = false }: { correctionId: string; compact?: boolean }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const foot = useRef<HTMLDivElement>(null)

  const q = useQuery({ queryKey: ['correction', spoke, correctionId], queryFn: () => correctionsApi.detail(spoke, correctionId) })
  const say = useMutation({
    mutationFn: (words: string) => correctionsApi.addTurn(spoke, correctionId, words),
    onSuccess: () => {
      setText('')
      void qc.invalidateQueries({ queryKey: ['correction', spoke, correctionId] })
      void qc.invalidateQueries({ queryKey: ['improve'] })
      void qc.invalidateQueries({ queryKey: ['ledger'] })
    },
  })

  const turns: Turn[] = q.data?.turns ?? []
  const hypothesis = q.data?.correction.hypothesis ?? null
  useEffect(() => { foot.current?.scrollIntoView({ block: 'nearest' }) }, [turns.length, say.isPending])

  const enough = text.trim().length > 0

  return (
    <div className={`correct-thread${compact ? ' compact' : ''}`}>
      {q.isPending && <p className="muted small">Loading the discussion…</p>}
      {turns.length === 0 && !q.isPending && (
        <p className="muted small">
          Nothing said yet. The correction and its reason are already on the ledger; say more here when the reason alone
          would not tell the next reader what to do differently.
        </p>
      )}
      <ol className="turns">
        {turns.map((t) => (
          <li key={t.turnId} className={`turn ${t.role}`}>
            <span className="turn-who">{t.role === 'human' ? 'You' : 'Scout'}</span>
            <p className="turn-text">{t.text}</p>
            <time className="muted small" dateTime={t.at}>{formatRelative(t.at)}</time>
          </li>
        ))}
        {say.isPending && (
          <li className="turn human pending">
            <span className="turn-who">You</span>
            <p className="turn-text">{text}</p>
            <time className="muted small">sending…</time>
          </li>
        )}
      </ol>

      {hypothesis && (
        <div className="hypothesis" role="note">
          <p className="kicker">What the discussion concluded</p>
          <dl className="facts compact">
            {factsOf(hypothesis).map((f) => (
              <Fragment key={f.label}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </Fragment>
            ))}
          </dl>
          <p className="muted small">This is what tonight's run reads. It is a reading of your words, not a rule yet — nothing changes until it is verified.</p>
        </div>
      )}

      {say.isError && <p className="error small">{say.error instanceof RefusedError ? say.error.refusal.message : (say.error as Error).message}</p>}

      <form
        className="turn-compose"
        onSubmit={(ev) => { ev.preventDefault(); if (enough && !say.isPending) say.mutate(text.trim()) }}
      >
        <textarea
          rows={compact ? 2 : 3}
          value={text}
          onChange={(ev) => setText(ev.target.value)}
          placeholder="What did Scout miss? Say it the way you would say it to a colleague."
          aria-label="Say more about why this is wrong"
        />
        <button type="submit" className="button primary small" disabled={!enough || say.isPending}>{say.isPending ? 'Sending…' : 'Send'}</button>
      </form>
      <div ref={foot} />
    </div>
  )
}
